import { motion } from "framer-motion";
import { Syringe, HeartPulse, Bone, SprayCan, Apple, Pill, Star } from "lucide-react";
import { siteContent } from "@/content/site";

const CAT_ICONS = {
  syringe: Syringe,
  heartpulse: HeartPulse,
  bone: Bone,
  spray: SprayCan,
  apple: Apple,
  pill: Pill,
};

const { healthStore } = siteContent;

export default function HealthStore() {
  return (
    <section
      id="store"
      data-testid="store-section"
      className="relative py-20 lg:py-28 bg-[#E1EEEA]/60"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E8A33D]">
            {healthStore.eyebrow}
          </span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0E2A36] leading-tight">
            {healthStore.headingLead}{" "}
            <em className="italic font-medium text-[#1F4E5F]">
              {healthStore.headingItalic}
            </em>
          </h2>
          <p className="mt-4 text-[#3D5560] text-lg leading-relaxed">
            {healthStore.body}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 bg-white border border-[#C9DDD6] rounded-3xl p-7 lg:p-9"
          >
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8A33D] mb-5">
              {healthStore.categoriesLabel}
            </p>
            <div className="space-y-3">
              {healthStore.categories.map((c, i) => {
                const Icon = CAT_ICONS[c.iconKey] || Pill;
                return (
                  <div
                    key={i}
                    className="flex items-start gap-4 p-3 rounded-2xl hover:bg-[#E1EEEA] transition-colors cursor-default group"
                    data-testid={`store-category-${i}`}
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#1F4E5F] text-[#F4FAF8] flex items-center justify-center shrink-0 group-hover:bg-[#E8A33D] transition-colors">
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0E2A36] text-[15px]">{c.name}</p>
                      <p className="text-sm text-[#6A8290] leading-snug">{c.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <div className="lg:col-span-7 flex flex-col gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-[#1F4E5F] text-[#F4FAF8] rounded-3xl p-7 lg:p-9 relative overflow-hidden"
            >
              <div className="grain absolute inset-0 pointer-events-none" />
              <div className="relative">
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8A33D]">
                  {healthStore.recsEyebrow}
                </span>
                <h3 className="font-display mt-2 text-2xl lg:text-3xl font-semibold">
                  {healthStore.recsTitle}
                </h3>
                <p className="mt-3 text-[#F4FAF8]/75 text-[15px]">
                  {healthStore.recsBody}
                </p>
                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {healthStore.recs.map((r, i) => (
                    <div
                      key={i}
                      className="rounded-2xl bg-white/8 border border-white/10 p-4 backdrop-blur-sm"
                    >
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#E8A33D]">
                        {r.c}
                      </p>
                      <p className="mt-1 text-sm text-[#F4FAF8]/90">{r.p}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white border-2 border-[#E8A33D]/30 rounded-3xl p-7 lg:p-9 relative"
              data-testid="featured-bundle"
            >
              <span className="inline-flex items-center gap-1.5 bg-[#E8A33D] text-white px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider">
                <Star className="w-3 h-3 fill-white" /> {healthStore.bundleBadge}
              </span>
              <h3 className="font-display mt-3 text-2xl lg:text-3xl font-semibold text-[#0E2A36]">
                {healthStore.bundleTitle}
              </h3>
              <p className="mt-2 text-[#3D5560]">{healthStore.bundleBody}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {healthStore.kitItems.map((k, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 rounded-full bg-[#E1EEEA] border border-[#C9DDD6] text-sm text-[#0E2A36] font-medium"
                  >
                    {k}
                  </span>
                ))}
              </div>
              <div className="mt-6 flex items-end justify-between">
                <div>
                  <p className="font-display text-3xl lg:text-4xl font-bold text-[#1F4E5F]">
                    {healthStore.bundlePrice}
                  </p>
                  <p className="text-sm text-[#6A8290]">{healthStore.bundlePriceNote}</p>
                </div>
                <button
                  data-testid="featured-bundle-cta"
                  onClick={() =>
                    document.getElementById("beta")?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="px-5 py-2.5 rounded-full bg-[#1F4E5F] hover:bg-[#173E50] text-[#F4FAF8] text-sm font-semibold transition-colors"
                >
                  {healthStore.bundleCta}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
